import type { HelpGuide } from '../types';

export const leaseGuide: HelpGuide = {
  key: 'lease',
  title: 'สัญญาเช่า TFRS 16 (Leases)',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ สัญญาเช่า TFRS 16',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/lease/คู่มือการใช้งาน_สัญญาเช่า-TFRS16_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “สัญญาเช่า TFRS 16” ใช้คุมสัญญาเช่าระยะยาว (เช่ารถ เช่าที่ดิน/อาคาร เช่าเครื่องจักร) ตามมาตรฐาน TFRS 16 ซึ่งกำหนดให้ผู้เช่าบันทึก “สินทรัพย์สิทธิการใช้ (ROU Asset)” คู่กับ “หนี้สินตามสัญญาเช่า” แล้วทยอยคิด ดอกเบี้ย + ค่าเสื่อมราคา ROU ทุกเดือน',
        },
        {
          kind: 'callout',
          text: 'หลักการ TFRS 16: เริ่มสัญญา → Dr ROU Asset / Cr หนี้สินตามสัญญาเช่า (= มูลค่าปัจจุบัน PV ของค่าเช่า) · ทุกเดือน → Dr ดอกเบี้ยจ่าย + Dr ค่าเสื่อม ROU / Cr หนี้สิน + Cr ค่าเสื่อมสะสม · จ่ายค่าเช่า → Dr หนี้สิน / Cr เงินสด',
        },
        { kind: 'para', text: 'หน้าจอมี 5 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ทะเบียนสัญญาเช่า — ',
              text: 'รายการสัญญาทั้งหมด + ตัวกรอง + KPI (แท็บเริ่มต้น)',
            },
            {
              lead: 'ตารางตัดจ่ายสัญญา — ',
              text: 'ตารางดอกเบี้ย + เงินต้น + ค่าเสื่อม ROU ทีละงวด',
            },
            { lead: 'ประมวลผลรายเดือน — ', text: 'ลงบัญชีดอกเบี้ย + ค่าเช่า + ค่าเสื่อมของงวด' },
            {
              lead: 'เปรียบเทียบบัญชี vs ภาษี — ',
              text: 'เทียบค่าใช้จ่ายบัญชี (TFRS16) กับภาษี + ภาษีเงินได้รอตัดบัญชี (DTA)',
            },
            {
              lead: 'หมายเหตุประกอบงบ (Disclosure) — ',
              text: 'หมายเหตุ ROU/หนี้สิน + วิเคราะห์การครบกำหนด + P&L',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/lease/01-register.png',
          caption:
            'ภาพรวม — KPI (ROU/หนี้สิน/ค่าเสื่อม/ดอกเบี้ย), สรุปการบันทึกบัญชี TFRS 16 และตารางทะเบียนสัญญาเช่า',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'สินทรัพย์สิทธิการใช้ (ROU) — สุทธิ — ',
              text: 'มูลค่า ROU คงเหลือหลังหักค่าเสื่อม',
            },
            {
              lead: 'หนี้สินตามสัญญาเช่า — ',
              text: 'ยอดหนี้สินคงเหลือ (แยกหมุนเวียน/ไม่หมุนเวียน)',
            },
            {
              lead: 'ค่าเสื่อมราคา ROU (YTD) — ',
              text: 'ค่าเสื่อม ROU ที่บันทึกในปีนี้ (เส้นตรง)',
            },
            {
              lead: 'ดอกเบี้ยจ่าย — สัญญาเช่า (YTD) — ',
              text: 'ดอกเบี้ยที่บันทึกในปีนี้ (วิธี Effective Interest)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ใต้ KPI มีแผง “หลักการบันทึกบัญชีตาม TFRS 16” สรุปใบสำคัญมาตรฐาน 4 แบบ ปุ่มมุมขวาบน: เลือกปี · Export Disclosure · ลงทะเบียนสัญญาใหม่ (ปุ่มสีแดง) ตารางทะเบียนกรองด้วย ค้นหา/ประเภท/หน่วยงาน/สถานะ คอลัมน์: เลขที่สัญญา · ผู้ให้เช่า/สินทรัพย์ · ประเภท · วันเริ่ม-สิ้นสุด · งวด · ค่าเช่า/เดือน · IBR · ROU เริ่มต้น-คงเหลือ · หนี้สินคงเหลือ · สถานะ — คลิกที่แถวเพื่อดูตารางคำนวณ',
        },
      ],
    },
    {
      id: 'add',
      heading: '3. การลงทะเบียนสัญญาเช่าใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม ลงทะเบียนสัญญาใหม่ แล้วกรอก: ประเภทสัญญาเช่า (รถ/ที่ดิน-อาคาร/เครื่องจักร) · ผู้ให้เช่า · สาขา/หน่วยงาน · รายละเอียดสินทรัพย์ · อายุสัญญา (เดือน) · ค่าเช่า/เดือน · IBR (อัตราดอกเบี้ยเงินกู้ส่วนเพิ่ม %/ปี) · วันเริ่มสัญญา',
        },
        {
          kind: 'para',
          text: 'ระบบคำนวณ มูลค่าปัจจุบัน (PV) = ROU + หนี้สินเริ่มต้น ให้แบบเรียลไทม์ พร้อมตัวอย่างตารางตัดจ่าย 4 งวดแรก และใบสำคัญรับรู้สัญญาเช่าครั้งแรก (Day 1) รองรับ ค่าเช่าขั้นบันได (Stepped rent), ความถี่การจ่าย (รายเดือน/ไตรมาส/ปี/ครั้งเดียว) และตั้งค่าขั้นสูง (day-count + อัตราภาษี)',
        },
        {
          kind: 'image',
          src: '/help/lease/02-add.png',
          caption:
            'ฟอร์มลงทะเบียนสัญญาเช่า — คำนวณ PV (ROU+หนี้สิน), ตัวอย่างตารางตัดจ่าย และใบสำคัญ Day-1',
        },
      ],
    },
    {
      id: 'schedule',
      heading: '4. ตารางตัดจ่ายสัญญา',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ตารางตัดจ่ายสัญญา (หรือคลิกแถวในทะเบียน) แสดงการ์ดสรุปสัญญา + ตารางคำนวณทีละงวด: งวด · เดือน · ยอดหนี้สินยกมา · ค่าเช่าจ่าย · ดอกเบี้ย · เงินต้น · ยอดหนี้สินยกไป · ค่าเสื่อม ROU · ROU คงเหลือ · สถานะ (Posted / งวดปัจจุบัน / Forecast)',
        },
        {
          kind: 'image',
          src: '/help/lease/03-schedule.png',
          caption: 'ตารางตัดจ่ายสัญญา — ดอกเบี้ย + เงินต้น + ค่าเสื่อมราคา ROU ทีละงวด พร้อมสถานะ',
        },
      ],
    },
    {
      id: 'monthly',
      heading: '5. การประมวลผลรายเดือน',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ประมวลผลรายเดือน รวมทุกสัญญาที่ต้องลงบัญชีในงวด แสดงดอกเบี้ย · เงินต้น · รวมจ่ายเงินสด · ค่าเสื่อม ROU · รวมผลกระทบ P&L พร้อมพรีวิวใบสำคัญ (JV ดอกเบี้ย+ค่าเช่า และ JV ค่าเสื่อม ROU) กดปุ่ม ประมวลผล & ลง JV เพื่อลงบัญชีทั้งงวด',
        },
        {
          kind: 'image',
          src: '/help/lease/06-monthly.png',
          caption:
            'ประมวลผลรายเดือน — รายการที่จะลงบัญชีทุกสัญญา และพรีวิวใบสำคัญ ดอกเบี้ย+ค่าเช่า / ค่าเสื่อม ROU',
        },
      ],
    },
    {
      id: 'modify',
      heading: '6. การปรับปรุงสัญญา (Remeasurement)',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อมีการเปลี่ยนแปลงค่าเช่าระหว่างอายุสัญญา (เช่น เจรจาลดค่าเช่า) ที่แท็บตารางตัดจ่ายให้กดปุ่ม ปรับปรุงสัญญา ระบุ วันที่มีผล · Revised IBR · ค่าเช่าใหม่ ระบบจะ remeasure หนี้สินและปรับ ROU ใหม่ (ไม่ลง P&L) แสดงผลกระทบ หนี้สินเดิม→ใหม่ และใบสำคัญปรับปรุง (Remeasurement JV) แล้วกด ยืนยันปรับปรุงสัญญา',
        },
        {
          kind: 'image',
          src: '/help/lease/04-modify.png',
          caption: 'ปรับปรุงสัญญา (Remeasurement) — ผลกระทบหนี้สิน/ROU และใบสำคัญปรับปรุง',
        },
      ],
    },
    {
      id: 'terminate',
      heading: '7. การยกเลิกสัญญาก่อนกำหนด',
      blocks: [
        {
          kind: 'para',
          text: 'ที่แท็บตารางตัดจ่ายให้กดปุ่ม ยกเลิกสัญญา ระบุ วันที่ยกเลิก + ค่าปรับ (ถ้ามี) ระบบจะตัดรายการ ROU + หนี้สินที่เหลือ และรับรู้ กำไร/ขาดทุน จากการยกเลิก แสดงผลกระทบ (หนี้สินคงเหลือ/ROU NBV/กำไรขาดทุน) และใบสำคัญตัดรายการ (Derecognition JV) แล้วกด ยืนยันยกเลิกสัญญา (สถานะเปลี่ยนเป็น “ยกเลิก”)',
        },
        {
          kind: 'image',
          src: '/help/lease/05-terminate.png',
          caption:
            'ยกเลิกสัญญาก่อนกำหนด — ผลกระทบ + กำไร/ขาดทุน และใบสำคัญตัดรายการ (Derecognition JV)',
        },
      ],
    },
    {
      id: 'disclosure',
      heading: '8. หมายเหตุประกอบงบ (Disclosure)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ หมายเหตุประกอบงบ จัดทำหมายเหตุตาม TFRS 16 ให้พร้อม: (1) ROU แยกตามประเภท (ราคาทุน/ค่าเสื่อมสะสม/NBV) (2) หนี้สินตามสัญญาเช่าแยกระยะสั้น/ยาว (3) การวิเคราะห์การครบกำหนดของหนี้สิน (maturity: ≤1ปี/1-2/2-5/เกิน 5 ปี) (4) รายการในกำไรขาดทุน (ดอกเบี้ย/ค่าเสื่อม ROU/ค่าเช่าระยะสั้น/มูลค่าต่ำ)',
        },
        {
          kind: 'image',
          src: '/help/lease/07-disclosure.png',
          caption:
            'หมายเหตุประกอบงบ — ROU ตามประเภท, หนี้สินสั้น/ยาว, การวิเคราะห์การครบกำหนด และผลกระทบ P&L',
        },
      ],
    },
    {
      id: 'tax',
      heading: '9. เปรียบเทียบบัญชี vs ภาษี (DTA)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ เปรียบเทียบบัญชี vs ภาษี เทียบค่าใช้จ่ายทางบัญชี (TFRS 16 = ดอกเบี้ย + ค่าเสื่อม ROU) กับค่าใช้จ่ายทางภาษี (= ค่าเช่าที่จ่ายจริง ตาม ม.65) รายปี แล้วคำนวณผลต่างสะสมเป็น ภาษีเงินได้รอตัดบัญชี (DTA) ตลอดอายุสัญญา (รายการนี้ใช้ได้กับสัญญาที่บันทึกผ่านระบบจริงเท่านั้น)',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '10. สรุปสถานะของสัญญา',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['เริ่มต้น', 'ลงทะเบียนแล้วแต่ยังไม่เริ่มตัดจ่าย'],
            ['กำลังมีผล', 'สัญญาที่กำลังทยอยคิดดอกเบี้ย/ค่าเสื่อม (ปรับปรุง/ยกเลิกได้)'],
            ['สิ้นสุด', 'ครบอายุสัญญาแล้ว'],
            ['ยกเลิก', 'ยกเลิกก่อนกำหนด — ตัด ROU + หนี้สินแล้ว'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '11. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'IBR คืออะไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'อัตราดอกเบี้ยเงินกู้ส่วนเพิ่มของผู้เช่า ใช้คิดลดค่าเช่าในอนาคตเป็นมูลค่าปัจจุบัน (PV)',
            },
          ],
        },
        { kind: 'para', text: 'ลงบัญชีดอกเบี้ย/ค่าเสื่อมรายเดือนอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            { text: 'ที่แท็บ ประมวลผลรายเดือน กดปุ่ม ประมวลผล & ลง JV ระบบลงใบสำคัญทุกสัญญาในงวด' },
          ],
        },
        { kind: 'para', text: 'ปุ่ม “ปรับปรุงสัญญา/ยกเลิกสัญญา” กดไม่ได้?' },
        {
          kind: 'bullets',
          items: [{ text: 'ทำได้เฉพาะสัญญาที่สถานะ “กำลังมีผล” เท่านั้น' }],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '12. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'TFRS 16 — ',
              text: 'มาตรฐานการรายงานทางการเงินเรื่องสัญญาเช่า (ผู้เช่าบันทึก ROU + หนี้สิน)',
            },
            {
              lead: 'ROU Asset — ',
              text: 'สินทรัพย์สิทธิการใช้ (Right-of-Use) ที่เกิดจากสัญญาเช่า',
            },
            {
              lead: 'หนี้สินตามสัญญาเช่า — ',
              text: 'ภาระผูกพันจ่ายค่าเช่าในอนาคต คิดลดเป็นมูลค่าปัจจุบัน',
            },
            { lead: 'IBR — ', text: 'อัตราดอกเบี้ยเงินกู้ส่วนเพิ่ม ใช้คิดลด (discount rate)' },
            {
              lead: 'Remeasurement — ',
              text: 'การปรับวัดหนี้สิน/ROU ใหม่เมื่อเงื่อนไขเปลี่ยน (ไม่ลง P&L)',
            },
            {
              lead: 'Derecognition — ',
              text: 'การตัดรายการ ROU + หนี้สินออกเมื่อยกเลิกสัญญา รับรู้กำไร/ขาดทุน',
            },
            {
              lead: 'DTA — ',
              text: 'ภาษีเงินได้รอตัดบัญชี (Deferred Tax Asset) จากผลต่างบัญชี vs ภาษี',
            },
          ],
        },
      ],
    },
  ],
};
